import { FaTimes, FaTrash, FaEdit } from "react-icons/fa";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectRe_RenderedState, setSharedState } from "../../features/sharedStateSlices/shareState";
import { makeFetchRequest, makePutRequest } from "../../utilities/common";
import { defaultUrl } from "../../features/actions";
import loader from "../../assets/loader.gif";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import axios from "axios";

const PRIMARY = "#0F2A6B";

export const AddParentForm = ({
  numberOfParents = 0,
  form,
  addParent,
  handleParentSubmit,
  handleParentChange,
  editForm,
}: any) => {
  const dispatch = useDispatch();
  const reloaded = useSelector((state) => selectRe_RenderedState(state));
  console.log(editForm);

  const [existingParents, setExistingParents] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(false);
  const [isEditingFound, setIsEditingFound] = useState(false);
  const [isUpdatingParent, setIsUpdatingParent] = useState(false);

  const saveExistingParentEdits = async (idx: number) => {
    if (!existingParents?.id) return;
    const currentParentData = form[idx] || {};

    toast.remove();
    toast.loading("Updating parent details...");
    setIsUpdatingParent(true);

    try {
      const payload = {
        data: [
          {
            parent_id: existingParents.id,
            name: currentParentData.name || existingParents.name,
            phone: currentParentData.phone || existingParents.phone,
            home_number: currentParentData.home_number || existingParents.home_number,
            work_number: currentParentData.work_number || existingParents.work_number,
            relationship: currentParentData.relationship || existingParents.relationship,
          },
        ],
      };

      await makePutRequest(`${defaultUrl}/parents`, payload);

      toast.remove();
      toast.success("Parent details updated successfully!");

      // Refetch parent with current email to update existingParents state with current name
      const searchEmail = currentParentData.email || existingParents.email;
      if (searchEmail) {
        const result = await makeFetchRequest(
          `${defaultUrl}/parents?email=${searchEmail}`,
          false,
          true,
        );
        if (result && result[0]?.email) {
          setExistingParents(result[0]);
        }
      }

      // Mark parent as selected with updated details and close edit input fields without closing modal
      setSelected(true);
      setIsEditingFound(false);
      dispatch(setSharedState());
    } catch (error: any) {
      toast.remove();
      toast.error(error?.response?.data?.message || "Failed to update parent details");
    } finally {
      setIsUpdatingParent(false);
    }
  };

  async function checkForExistingParent(email: string) {
    setExistingParents(null);
    setIsEditingFound(false);
    setSelected(false);
    setLoading(true);

    const result = await makeFetchRequest(
      `${defaultUrl}/parents?email=${email}`,
      false,
      true,
    );

    if (result && result[0]?.email) {
      setExistingParents(result[0]);
    }

    setLoading(false);
  }

  const token = sessionStorage.getItem("token")?.replace(/['"]+/g, "");

  const deleteParentPermanently = async (parentId: string, parentName?: string) => {
    const confirmResult = await Swal.fire({
      title: "Delete Parent Permanently?",
      text: `Are you sure you want to permanently delete ${
        parentName ? `"${parentName}"` : "this parent"
      } from the system? The parent will no longer have access to any of the linked children.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete Parent",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        const headers = {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        };
        try {
          try {
            await axios.delete(`${defaultUrl}/parents/delete/${parentId}`, { headers });
          } catch {
            await axios.delete(`${defaultUrl}/parents/${parentId}`, { headers });
          }
          return true;
        } catch (error) {
          Swal.showValidationMessage("Failed to delete parent from system");
          return false;
        }
      },
      customClass: {
        popup: "rounded-2xl shadow-xl border border-slate-100",
        confirmButton: "rounded-xl font-semibold px-4 py-2 text-xs",
        cancelButton: "rounded-xl font-semibold px-4 py-2 text-xs",
      },
    });

    if (confirmResult.isConfirmed) {
      toast.success("Parent deleted successfully from the system");
      setExistingParents(null);
      setSelected(false);
      dispatch(setSharedState());
    }
  };

  useEffect(() => {
    return () => {
      setExistingParents(null);
      setSelected(false);
      setIsEditingFound(false);
      if (addParent) {
        addParent([
          {
            name: "",
            email: "",
            phone: "",
            home_number: "",
            work_number: "",
            relationship: "",
          },
        ]);
      }
    };
  }, []);

  return (
    <div className="bg-blue-50 p-6 rounded-2xl">
      <form
        onSubmit={(e: any) => {
          handleParentSubmit(e, existingParents);
        }}
        className="space-y-6"
      >
        {form.map((parent: any, idx: number) => (
          <div
            key={idx}
            className="bg-white rounded-2xl p-5 border shadow-sm hover:shadow-md transition space-y-4"
          >
            {/* HEADER */}
            <div className="flex justify-between items-center">
              <h2 className="text-sm font-semibold text-gray-700">
                Parent / Guardian {idx + 1 + numberOfParents}
              </h2>

              <FaTimes
                onClick={() =>
                  addParent((prev: any) =>
                    prev.filter((_: any, i: number) => i !== idx),
                  )
                }
                className={`text-red-500 cursor-pointer ${
                  idx === 0 ? "hidden" : ""
                }`}
              />
            </div>

            {/* EMAIL */}
            {!selected && (
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  name="email"
                  value={parent?.email || ""}
                  onChange={(event) => {
                    checkForExistingParent(event.target.value);
                    handleParentChange(idx, event);
                  }}
                  required
                  className="input"
                />

                {loading && (
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                    <img src={loader} className="w-4 h-4" />
                    checking if parent exists...
                  </div>
                )}
              </div>
            )}

            {/* FOUND   */}
            {existingParents && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex justify-between items-center">
                <div>
                  <p className="text-xs text-green-700 font-semibold">Parent Found</p>
                  <p className="font-medium text-sm text-slate-800">{existingParents.name}</p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="px-3 py-1.5 rounded-md text-xs font-semibold text-white transition shadow-sm"
                    style={{ backgroundColor: PRIMARY }}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelected(true);
                      setIsEditingFound(false);
                    }}
                  >
                    {selected && !isEditingFound ? "Selected" : "Select"}
                  </button>

                  <button
                    type="button"
                    className="px-3 py-1.5 rounded-md text-xs font-semibold text-amber-800 bg-amber-100 hover:bg-amber-200 transition flex items-center gap-1 shadow-sm border border-amber-300"
                    onClick={(e) => {
                      e.preventDefault();
                      if (existingParents) {
                        ["name", "phone", "home_number", "work_number", "relationship"].forEach((field) => {
                          handleParentChange(idx, {
                            target: { name: field, value: existingParents[field] || "" },
                          });
                        });
                      }
                      setIsEditingFound(true);
                      setSelected(true);
                    }}
                  >
                    <FaEdit size={11} />
                    Edit Parent
                  </button>

                  <button
                    type="button"
                    className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-red-600 hover:bg-red-700 transition flex items-center gap-1 shadow-sm"
                    onClick={(e) => {
                      e.preventDefault();
                      deleteParentPermanently(existingParents.id, existingParents.name);
                    }}
                  >
                    <FaTrash size={10} />
                    Delete Parent
                  </button>
                </div>
              </div>
            )}

            {/* FORM */}
            {(!selected || isEditingFound) && (
              <>
                {isEditingFound && (
                  <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3.5 py-2 rounded-xl font-medium flex items-center justify-between">
                    <span>Editing profile details for <strong>{existingParents?.name}</strong></span>
                    <button
                      type="button"
                      onClick={() => setIsEditingFound(false)}
                      className="text-amber-800 underline hover:text-amber-950 text-[11px] font-semibold ml-2"
                    >
                      Hide Inputs
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Full Name"
                    name="name"
                    value={parent?.name || ""}
                    onChange={(e: any) => handleParentChange(idx, e)}
                  />

                  <Input
                    label="Mobile Number"
                    name="phone"
                    value={parent?.phone || ""}
                    onChange={(e: any) => handleParentChange(idx, e)}
                  />

                  <Input
                    label="Home Number"
                    name="home_number"
                    value={parent?.home_number || ""}
                    onChange={(e: any) => handleParentChange(idx, e)}
                  />

                  <Input
                    label="Work Number"
                    name="work_number"
                    value={parent?.work_number || ""}
                    onChange={(e: any) => handleParentChange(idx, e)}
                  />
                </div>

                {/* RELATIONSHIP */}
                <div>
                  <p className="text-xs text-gray-500 mb-1">Relationship</p>

                  <div className="flex gap-2">
                    {["Parent", "Guardian"].map((r) => (
                      <div
                        key={r}
                        onClick={() =>
                          handleParentChange(idx, {
                            target: {
                              name: "relationship",
                              value: r,
                            },
                          })
                        }
                        className="px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition"
                        style={{
                          backgroundColor:
                            parent.relationship === r ? PRIMARY : "",
                          color: parent.relationship === r ? "white" : "",
                        }}
                      >
                        {r}
                      </div>
                    ))}
                  </div>
                </div>

                {isEditingFound && (
                  <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                    <button
                      type="button"
                      onClick={() => setIsEditingFound(false)}
                      className="px-3.5 py-1.5 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 hover:bg-slate-100 transition"
                    >
                      Cancel Edit
                    </button>

                    <button
                      type="button"
                      disabled={isUpdatingParent}
                      onClick={() => saveExistingParentEdits(idx)}
                      className="px-4 py-1.5 rounded-lg text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 transition shadow-sm flex items-center gap-1.5 disabled:opacity-50"
                    >
                      {isUpdatingParent ? "Updating..." : "Update Parent Profile"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ))}

        {/* ADD BUTTON */}
        {numberOfParents < 1 && (
          <button
            type="button"
            onClick={() =>
              addParent((prev: any) => [
                ...prev,
                {
                  name: "",
                  email: "",
                  phone: "",
                  home_number: "",
                  work_number: "",
                  relationship: "",
                },
              ])
            }
            className="px-4 py-2 border rounded-lg text-sm hover:bg-blue-50"
          >
            + Add New Parent / Guardian
          </button>
        )}

        {/* SUBMIT */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-6 py-2.5 rounded-lg text-white font-medium hover:shadow-lg transition"
            style={{ backgroundColor: PRIMARY }}
          >
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

/* ================= INPUT ================= */

const Input = ({ label, ...props }: any) => (
  <div>
    <label className="label">{label}</label>
    <input {...props} className="input" />
  </div>
);

/* ================= SHARED STYLES ================= */

const styles = `
.input {
  width: 100%;
  margin-top: 4px;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  outline: none;
  transition: all 0.2s;
}
.input:focus {
  border-color: #0F2A6B;
  box-shadow: 0 0 0 2px rgba(15,42,107,0.1);
}

.label {
  font-size: 11px;
  color: #6b7280;
}
`;

if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.innerHTML = styles;
  document.head.appendChild(style);
}
